import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { getArticulosResumen, descargarExport } from "./api"
import { rangoDelMes } from "./helpers"
import HeaderFiltros from "./HeaderFiltros"
import DrawerDetalleProducto from "./DrawerDetalleProducto"
import ResumenTab from "./tabs/ResumenTab"
import ProductosTab from "./tabs/ProductosTab"
import ParetoTab from "./tabs/ParetoTab"
import ComparadorTab from "./tabs/ComparadorTab"
import LineasTab from "./tabs/LineasTab"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Inventarios", link: "#" },
  { title: "Análisis de Artículos", link: "#" },
]

const TABS = [
  { id: "resumen",    label: "Resumen",     icon: "mdi-view-dashboard-outline" },
  { id: "productos",  label: "Productos",   icon: "mdi-package-variant" },
  { id: "pareto",     label: "Pareto ABC",  icon: "mdi-podium-gold" },
  { id: "comparador", label: "Comparador",  icon: "mdi-compare-horizontal" },
  { id: "lineas",     label: "Líneas",      icon: "mdi-magnify-scan" },
]

const ahora = new Date()

const AnalisisArticulos = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")
  const [campoFecha, setCampoFecha] = useState("fecha")
  const [sucursal, setSucursal] = useState("")

  const [resumen, setResumen] = useState(null)
  const [loadingResumen, setLoadingResumen] = useState(false)
  const [error, setError] = useState("")
  const [tabActivo, setTabActivo] = useState("resumen")

  const [filtrosAplicados, setFiltrosAplicados] = useState(null)

  const [exportando, setExportando] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)

  const [drawerAbierto, setDrawerAbierto] = useState(false)
  const [productoActivo, setProductoActivo] = useState(null)

  // Para precargar comparador desde el drawer (botón "Comparar con co-purchases")
  const [precargarComparador, setPrecargarComparador] = useState(null)

  // Indicator de cache HIT/MISS (header X-Cache del back)
  const [cacheHit, setCacheHit] = useState(false)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Análisis de Artículos | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Análisis de Artículos"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  // Reset al cambiar empresa
  useEffect(() => {
    setResumen(null)
    setError("")
    setFiltrosAplicados(null)
    setSucursal("")
    setTabActivo("resumen")
  }, [nitemisor])

  const construirFiltros = useCallback(() => {
    const base = { nitemisor, campo_fecha: campoFecha, sucursal: sucursal || undefined }
    if (modo === "mes") {
      const { fecha_inicio, fecha_fin } = rangoDelMes(year, month)
      return { ...base, fecha_inicio, fecha_fin }
    }
    return { ...base, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin, campoFecha, sucursal])

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  const cargarResumen = useCallback(async (filtros) => {
    setLoadingResumen(true)
    setError("")
    try {
      const response = await getArticulosResumen(filtros)
      const data = response.data
      if (data?.success === false) {
        throw new Error(data?.message || data?.error || "Error en el servidor")
      }
      // El back envuelve los datos del análisis en `resumen` (también acepta `data` por compatibilidad)
      setResumen(data?.resumen || data?.data || data)
      // X-Cache: HIT|MISS — indica si el back devolvió desde caché
      const xCache = response.headers?.["x-cache"] || response.headers?.["X-Cache"]
      setCacheHit(xCache === "HIT")
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el resumen.")
      setResumen(null)
      setCacheHit(false)
    } finally {
      setLoadingResumen(false)
    }
  }, [])

  const handleConsultar = () => {
    const opts = construirFiltros()
    setFiltrosAplicados(opts)
    cargarResumen(opts)
  }

  const handleExportar = async (formato) => {
    if (!filtrosAplicados || exportando) return
    setExportando(formato)
    setExportInfo(null)
    setError("")
    try {
      const info = await descargarExport({ ...filtrosAplicados, formato })
      setExportInfo({ formato, totalRows: info.totalRows, filename: info.filename })
      setTimeout(() => setExportInfo(null), 6000)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const handleVerDetalle = (producto) => {
    setProductoActivo(producto)
    setDrawerAbierto(true)
  }

  // El drawer dispara este callback al hacer click en "Comparar con co-purchases":
  // - precarga la selección en ComparadorTab
  // - cierra el drawer
  // - cambia al tab Comparador
  const handleCompararDesdeDrawer = (productos) => {
    setPrecargarComparador(productos)
    setDrawerAbierto(false)
    setTabActivo("comparador")
  }

  if (!nitemisor) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Inventarios
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Análisis de Artículos
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span>
                <i className="mdi mdi-domain me-1 text-primary" />
                <strong>{empresaNombre}</strong> · NIT {nitemisor}
              </span>
              {cacheHit && (
                <span style={{
                  background: "#fef3c7", color: "#92400e",
                  padding: "1px 8px", borderRadius: 999,
                  fontSize: 11, fontWeight: 700,
                }}
                title="Respuesta servida desde caché del backend (más rápida)"
                >
                  <i className="mdi mdi-flash me-1" />
                  Desde caché
                </span>
              )}
            </div>
          </div>
          {filtrosAplicados && (
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className="btn btn-outline-success"
                onClick={() => handleExportar("xlsx")} disabled={!!exportando}>
                {exportando === "xlsx" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-excel me-1" /> Excel</>)}
              </button>
              <button type="button" className="btn btn-outline-primary"
                onClick={() => handleExportar("csv")} disabled={!!exportando}>
                {exportando === "csv" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-delimited me-1" /> CSV</>)}
              </button>
            </div>
          )}
        </div>

        <HeaderFiltros
          modo={modo} setModo={setModo}
          year={year} setYear={setYear} month={month} setMonth={setMonth}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          campoFecha={campoFecha} setCampoFecha={setCampoFecha}
          sucursal={sucursal} setSucursal={setSucursal}
          sucursalesDisponibles={[]}
          loading={loadingResumen}
          onConsultar={handleConsultar}
          puedeConsultar={puedeConsultar}
        />

        {exportInfo && (
          <div style={{
            marginTop: 12,
            background: "#d1fae5", border: "1px solid #a7f3d0",
            color: "#065f46", padding: "10px 14px", borderRadius: 8,
            fontSize: 13, display: "flex", alignItems: "center", gap: 10,
          }}>
            <i className="mdi mdi-check-circle" style={{ fontSize: 18 }} />
            <span>
              Descarga lista{exportInfo.totalRows ? `: ${exportInfo.totalRows.toLocaleString("es-CO")} filas` : ""}
              {exportInfo.filename ? ` · ${exportInfo.filename}` : ""}
            </span>
            <button type="button" className="btn-close ms-auto" style={{ fontSize: 10 }} onClick={() => setExportInfo(null)} />
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
          color: "#991b1b", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto"
            onClick={() => filtrosAplicados && cargarResumen(filtrosAplicados)}>
            Reintentar
          </button>
        </div>
      )}

      {/* Loading inicial */}
      {!resumen && loadingResumen && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando análisis…</div>
        </div>
      )}

      {/* Empty state */}
      {!resumen && !loadingResumen && !error && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-package-variant" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Configura el período y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Indica mes/año o rango de fechas y pulsa <strong>Consultar</strong> para ver el análisis
            de artículos de <strong>{empresaNombre}</strong>.
          </p>
        </div>
      )}

      {/* Tabs + contenido */}
      {filtrosAplicados && (resumen || tabActivo !== "resumen") && (
        <>
          <div style={{
            background: "#fff", borderRadius: "14px 14px 0 0",
            border: "1px solid #e2e8f0", borderBottom: "none",
            padding: "0 12px",
            display: "flex", gap: 4, overflowX: "auto",
          }}>
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              return (
                <button key={tab.id} type="button" onClick={() => setTabActivo(tab.id)} style={{
                  background: "none", border: "none", padding: "14px 18px",
                  fontSize: 13, fontWeight: 600,
                  color: activo ? "#0f172a" : "#64748b",
                  borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                }}>
                  <i className={`mdi ${tab.icon}`} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          <div>
            {tabActivo === "resumen"    && <ResumenTab resumen={resumen} />}
            {tabActivo === "productos"  && <ProductosTab filtros={filtrosAplicados} onVerDetalle={handleVerDetalle} />}
            {tabActivo === "pareto"     && <ParetoTab filtros={filtrosAplicados} />}
            {tabActivo === "comparador" && (
              <ComparadorTab
                filtros={filtrosAplicados}
                seleccionadosIniciales={precargarComparador}
                onPrecargado={() => setPrecargarComparador(null)}
              />
            )}
            {tabActivo === "lineas" && <LineasTab filtros={filtrosAplicados} />}
          </div>
        </>
      )}

      {/* Drawer detalle producto */}
      <DrawerDetalleProducto
        abierto={drawerAbierto}
        idProducto={productoActivo?.id_producto}
        productoBase={productoActivo}
        filtros={filtrosAplicados}
        onCerrar={() => setDrawerAbierto(false)}
        onCompararConCoPurchases={handleCompararDesdeDrawer}
      />
    </div>
  )
}

AnalisisArticulos.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(AnalisisArticulos)
